import type { DemoData } from "../domain/types";
import {
    getRuleIntegrationSourceRuleId,
    type DemoRuleLogicDefinition,
    type RuleLogicDefinitionMap,
} from "../rules/ruleLogicDefinitions";
import { getRuleIntegrationAdapter } from "./ruleIntegrationRegistry";
import type {
    AnyDemoRuleIntegrationAdapter,
    AppGlueStorageBackedConfig,
    RuleDemoDataProjection,
    RuleIntegrationEvaluation,
} from "./ruleIntegrationTypes";
import {
    createRuleIntegrationConfig,
    evaluateRuleIntegration,
    hydrateRuleIntegrationConfig,
    projectRuleIntegrationDemoData,
} from "./ruleIntegrationTypes";

export type RuleIntegrationConfigSource = "seeded_factory" | "saved_storage" | "saved_storage_fallback";

export type RuleIntegrationRuntimeEntry = {
    adapter: AnyDemoRuleIntegrationAdapter;
    definition: DemoRuleLogicDefinition;
    config: AppGlueStorageBackedConfig;
    configSource: RuleIntegrationConfigSource;
};

export type RuleIntegrationRuntimeRegistry = Record<string, RuleIntegrationRuntimeEntry>;

export const createRuleIntegrationRuntimeRegistry = (
    ruleDefinitionsById: RuleLogicDefinitionMap,
    data: DemoData,
): RuleIntegrationRuntimeRegistry => {
    return Object.fromEntries(
        Object.values(ruleDefinitionsById).flatMap((definition): [string, RuleIntegrationRuntimeEntry][] => {
            const adapter = getRuleIntegrationAdapter(getRuleIntegrationSourceRuleId(definition));
            if (!adapter) return [];

            const runtimeConfig = (() => {
                if (!definition.appGlueStorageData) {
                    return {
                        config: createRuleIntegrationConfig(adapter, { data }),
                        configSource: "seeded_factory" as const,
                    };
                }

                try {
                    return {
                        config: hydrateRuleIntegrationConfig(adapter, definition.appGlueStorageData),
                        configSource: "saved_storage" as const,
                    };
                } catch {
                    return {
                        config: createRuleIntegrationConfig(adapter, { data }),
                        configSource: "saved_storage_fallback" as const,
                    };
                }
            })();

            return [[
                definition.ruleId,
                {
                    adapter,
                    definition,
                    config: runtimeConfig.config,
                    configSource: runtimeConfig.configSource,
                },
            ]];
        }),
    );
};

export const evaluateRuleIntegrationRuntime = (
    runtimeRegistry: RuleIntegrationRuntimeRegistry,
    ruleId: string,
    row: unknown,
): RuleIntegrationEvaluation => {
    const runtime = runtimeRegistry[ruleId];
    if (!runtime) {
        throw new Error(`No AppGlue runtime adapter is registered for ${ruleId}.`);
    }

    return evaluateRuleIntegration(runtime.adapter, runtime.config, row, { definition: runtime.definition });
};

export const projectRuleIntegrationRuntimeDemoData = (
    runtimeRegistry: RuleIntegrationRuntimeRegistry,
): RuleDemoDataProjection =>
    Object.values(runtimeRegistry).reduce<RuleDemoDataProjection>((projection, runtime) => {
        const adapterProjection = projectRuleIntegrationDemoData(runtime.adapter, runtime.config);
        return {
            priceToleranceRules: adapterProjection?.priceToleranceRules ?? projection.priceToleranceRules,
            taxonomyMappings: adapterProjection?.taxonomyMappings ?? projection.taxonomyMappings,
        };
    }, {});

export const applyRuleIntegrationRuntimeDemoDataProjection = (
    data: DemoData,
    runtimeRegistry: RuleIntegrationRuntimeRegistry,
): DemoData => {
    const projection = projectRuleIntegrationRuntimeDemoData(runtimeRegistry);
    return {
        ...data,
        priceToleranceRules: projection.priceToleranceRules ?? data.priceToleranceRules,
        taxonomyMappings: projection.taxonomyMappings ?? data.taxonomyMappings,
    };
};
